import React, { useEffect, useState } from 'react';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import {
    Paper, Grid, Typography, TextField, MenuItem, Zoom, Table, TableBody,
    TableHead, TableContainer, TableCell, TableRow, Button, Dialog,
    DialogTitle, DialogContent, DialogContentText, DialogActions, CircularProgress,
    Accordion, AccordionSummary, AccordionDetails
} from '@material-ui/core/';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ArrowDropDownCircleIcon from '@material-ui/icons/ArrowDropDownCircle';
import { authFetch, logout } from '../../auth';
import { addMonths } from 'date-fns';
import * as XLSX from 'xlsx';

const StyledTableCell = withStyles((theme) => ({
    head: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.secondary.main,
        padding: 3
    },
    body: {
        fontSize: 14,
        color: theme.palette.primary.dark,
        padding: 5
    },
}))(TableCell);

const StyledTableRow = withStyles((theme) => ({
    root: {
        backgroundColor: theme.palette.action.hover,
    },
}))(TableRow);

const useStyles = makeStyles((theme) => ({
    root: {
        display: 'flex',
        flexWrap: 'wrap',
        padding: theme.spacing(2)
    },
    accordionStyle: {
        color: theme.palette.primary.main,
        border: "1px solid rgba(0, 0, 0, 0.12)",
    },
    sectionTitle: {
        marginBottom: 10,
        backgroundColor: theme.palette.secondary.light,
        borderRadius: 3,
        color: theme.palette.primary.dark
    },
    dialogContent: {
        fontSize: 14,
        color: theme.palette.primary.main,
    },
    dialogTitle: {
        fontSize: 14,
        color: theme.palette.primary.main,
        fontWeight: 'normal'
    },
    formText: {
        fontSize: 14,
        color: theme.palette.primary.main,
        textAlign: "left",
        marginLeft: theme.spacing(1)
    },
    customFilled: {
        '& .MuiFilledInput-root': {
            backgroundColor: '#ffffff',
            minHeight: 56,
            '&:hover': { backgroundColor: '#ffffff' },
            '&.Mui-focused': { backgroundColor: '#ffffff' },
        },
        '& .MuiFilledInput-input': {
            color: theme.palette.primary.main,
            fontWeight: 600,
        },
    },
}));

export default function Summary(props) {
    const classes = useStyles();
    const [feedbackDialog, setFeedbackDialog] = useState(false);
    const [feedbackMsg, setFeedbackMsg] = useState("");

    const [downloading, setDownloading] = useState(false);
    const [downloadingLegacy, setDownloadingLegacy] = useState(false);
    const [portfolios, setPortfolios] = useState([]);
    const [selectedPortfolio, setSelectedPortfolio] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const [balanceSections, setBalanceSections] = useState(null);
    const [enterpriseBalance, setEnterpriseBalance] = useState(null);

    const handleFeedbackDialogClose = () => {
        setFeedbackDialog(false);
    };

    const getFormattedPeriod = (dateObj, offsetMonths = 0) => {
        const d = addMonths(dateObj, offsetMonths);
        const monthNames = [
            "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
            "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"
        ];
        return `${monthNames[d.getMonth()]} - ${d.getFullYear()}`;
    };

    const thinBorder = {
        top: { style: "thin", color: { rgb: "D3D3D3" } },
        bottom: { style: "thin", color: { rgb: "D3D3D3" } },
        left: { style: "thin", color: { rgb: "D3D3D3" } },
        right: { style: "thin", color: { rgb: "D3D3D3" } }
    };

    const sectionHeaderStyle = {
        font: { bold: true, color: { rgb: "FFFFFF" }, size: 11 },
        fill: { fgColor: { rgb: "2E4053" } },
        alignment: { horizontal: "center", vertical: "center" },
        border: thinBorder
    };

    const cellStyle = {
        font: { size: 10 },
        alignment: { vertical: "center" },
        border: thinBorder
    };

    const autofitColumnsAndFormat = (worksheet, dataArray) => {
        if (!dataArray || dataArray.length === 0) return;

        const colWidths = [];
        dataArray.forEach((row) => {
            row.forEach((val, colIdx) => {
                let cellStr = "";
                if (val !== null && val !== undefined) {
                    if (typeof val === 'number') {
                        cellStr = val.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                    } else {
                        cellStr = String(val);
                    }
                }
                const len = cellStr.length;
                colWidths[colIdx] = Math.max(colWidths[colIdx] || 12, len + 4);
            });
        });
        worksheet['!cols'] = colWidths.map(w => ({ wch: Math.min(Math.max(w, 12), 60) }));

        const range = XLSX.utils.decode_range(worksheet['!ref'] || "A1");
        for (let R = range.s.r; R <= range.e.r; ++R) {
            for (let C = range.s.c; C <= range.e.c; ++C) {
                const cellRef = XLSX.utils.encode_cell({ r: R, c: C });
                const cell = worksheet[cellRef];
                if (cell) {
                    if (typeof cell.v === 'number') {
                        if (C === 2 && dataArray[0] && String(dataArray[0][C]).includes("$/kWh")) {
                            cell.z = '#,##0.00000';
                        } else {
                            cell.z = '#,##0.00';
                        }
                    } else if (typeof cell.v === 'string') {
                        if (cell.v === "Propósito") {
                            cell.s = { font: { bold: true, color: { rgb: "000000" } }, alignment: { vertical: "center" } };
                        } else if (cell.v.startsWith("Validación") || cell.v.includes("Conceptos a relacionar")) {
                            cell.s = { font: { italic: true, color: { rgb: "4A4A4A" } }, alignment: { vertical: "center", wrapText: true } };
                        } else if (cell.v.includes("Nota:") || cell.v.includes("NOTA")) {
                            cell.s = { font: { italic: true, bold: true, color: { rgb: "595959" } }, alignment: { vertical: "center", wrapText: true } };
                        }
                    }
                }
            }
        }
    };

    const fetchPortfolioData = async (mode = "STRICT_CORRECT") => {
        if (!selectedPortfolio || !enterpriseBalance) return null;
        
        const response = await authFetch(
            `/api/calc-portfolio-energy-balance?username=${props.username}&period=${props.periodDate.getMonth() + 1}&year=${props.periodDate.getFullYear()}&version=${props.tariffCalcVersion}&portfolio=${selectedPortfolio}&mode=${mode}`,
            {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ enterpriseBalance, mode })
            }
        );

        if (response.status === 422) {
            logout();
            return null;
        }
        return await response.json();
    };

    const generateExcelWorkbook = (sectionsData) => {
        const workbook = XLSX.utils.book_new();
        const currentPeriodStr = getFormattedPeriod(props.periodDate, 1);
        const prevPeriodStr = getFormattedPeriod(props.periodDate, 0);

        const staticText = {
            "Balance General": {
                proposito: "Validación Facturación Integral externa que recibe Cedenar por proveedores (Compra de energía, XM y otros)"
            },
            "Consumo y Demanda Comercial": {
                proposito: "Validación de la energía facturada"
            },
            "Compra de Energía por Contrato": {
                proposito: "Validación Compra de Energía por Contratos destinada exclusivamente a la atención del grupo ECP"
            },
            "Gestión de Energía en Bolsa": {
                proposito: "Validar el cubrimiento de la demanda del grupo ECP a través de contratos, compra de energía en bolsa y venta de excedentes en bolsa"
            },
            "Liquidación Mes Previo": {
                proposito: "Validación de la liquidación del mes previo en sus versiones preliminar y definitiva con el objetivo de establecer los deltas que se trasladan como ajustes en la prefacturación del mes actual"
            },
            "Liquidación Mes Actual": {
                proposito: "Validación de forma desagregada de los distintos conceptos facturados a las fronteras del grupo ECP dentro del mes m",
                notas: ["*Nota: Esta información refleja el neto del valor facturado para cada frontera"]
            },
            "Balance ECP": {
                notas: [
                    "*NOTA los valores que exceden el 100% son producto de la estimación de insumos para el cálculo de tarifas y la estimación del consumo. Se trasladaran como ajustes a favor o en contra de las fronteras de ECP en la siguiente liquidación",
                    "**NOTA entre los total de los conceptos del informe gerencial y del total facturado se debe a la exclusión del ajuste a la decena en este informe",
                    "",
                    "Conceptos a relacionar para obtener utilidad neta",
                    "Impuesto de Industria y Comercio con base en el estatuto de renta de cada municipio",
                    "Gravamen a movimientos financieros (4x1000)",
                    "Costo de Garantías MEM - Costo de Garantías por SDL y STR - ***Costo de Garantías Compra por Contrato - ****Garantía de cumplimiento",
                    "Costo Telemedida",
                    "Otros costos asociados a la prestación del servicio: aliado técnico, software, facturación electrónica",
                    "",
                    "*** NOTA En reunión de auditoría el día 30 de Julio se logró que los funcionarios de Ecopetrol acepten ayudar al reconocimiento de estos costos.",
                    "**** NOTA En reunión de auditoría el día 30 de Julio se logró que los funcionarios de Ecopetrol aprobaran disminución del costo de esta garantía por concepto de que no se ha contratado aún los 105 GWh objeto del contrato original.",
                    "",
                    "La utilidad mensual será el ingreso por comercialización menos todos los costos reflejados anteriormente"
                ]
            }
        };

        const applyStaticText = (sheetNameKey, wsRowsArray, mergesArray, colsCount) => {
            const textData = staticText[sheetNameKey];
            if (!textData) return;

            const safeCols = Math.max(0, colsCount - 1);

            if (textData.proposito) {
                wsRowsArray.unshift(
                    ["Propósito", ...Array(safeCols).fill("")],
                    [textData.proposito, ...Array(safeCols).fill("")],
                    Array(colsCount).fill("")
                );
                for (let i = 0; i < mergesArray.length; i++) {
                    mergesArray[i].s.r += 3;
                    mergesArray[i].e.r += 3;
                }
                mergesArray.push({ s: { r: 0, c: 0 }, e: { r: 0, c: safeCols } });
                mergesArray.push({ s: { r: 1, c: 0 }, e: { r: 1, c: safeCols } });
            }

            if (textData.notas && textData.notas.length > 0) {
                wsRowsArray.push(Array(colsCount).fill(""));
                textData.notas.forEach(nota => {
                    wsRowsArray.push([nota, ...Array(safeCols).fill("")]);
                    const lastRowIdx = wsRowsArray.length - 1;
                    mergesArray.push({ s: { r: lastRowIdx, c: 0 }, e: { r: lastRowIdx, c: safeCols } });
                });
            }
        };

        if (enterpriseBalance && enterpriseBalance.length > 0) {
            const bgRows = [["Concepto", "Compras kWh", "Compras $/kWh", "Compras $", "Ventas kWh", "Ventas $/kWh", "Ventas $"]];
            const bgMerges = [];
            
            enterpriseBalance.forEach((concept) => {
                const row = [concept.Name];
                if (Array.isArray(concept.Values)) {
                    concept.Values.forEach((val) => {
                        if (val !== null && val !== undefined && val !== "-") {
                            const num = Number(val);
                            row.push(!isNaN(num) ? num : val);
                        } else {
                            row.push("-");
                        }
                    });
                }
                bgRows.push(row);
            });

            applyStaticText("Balance General", bgRows, bgMerges, 7);

            const wsBG = XLSX.utils.aoa_to_sheet(bgRows);
            if (bgMerges.length > 0) wsBG['!merges'] = bgMerges;

            const offsetRow = staticText["Balance General"]?.proposito ? 3 : 0;
            bgRows[offsetRow].forEach((_, cIdx) => {
                const cellRef = XLSX.utils.encode_cell({ r: offsetRow, c: cIdx });
                if (!wsBG[cellRef]) wsBG[cellRef] = {};
                wsBG[cellRef].s = sectionHeaderStyle;
            });

            enterpriseBalance.forEach((concept, rIdx) => {
                const rowIndex = rIdx + 1 + offsetRow;
                if (Array.isArray(concept.Values)) {
                    const nameCellRef = XLSX.utils.encode_cell({ r: rowIndex, c: 0 });
                    if (wsBG[nameCellRef]) wsBG[nameCellRef].s = { ...cellStyle, alignment: { horizontal: "left" } };

                    concept.Values.forEach((val, cIdx) => {
                        const colIndex = cIdx + 1;
                        const cellRef = XLSX.utils.encode_cell({ r: rowIndex, c: colIndex });
                        if (wsBG[cellRef]) {
                            wsBG[cellRef].s = {
                                ...cellStyle,
                                alignment: { horizontal: typeof wsBG[cellRef].v === 'number' ? "right" : "center" }
                            };
                        }
                    });
                }
            });

            autofitColumnsAndFormat(wsBG, bgRows);
            XLSX.utils.book_append_sheet(workbook, wsBG, "Balance General");
        }

        const usedSheetNames = new Set(["Balance General"]);
        if (sectionsData && sectionsData.length > 0) {
            sectionsData.forEach((section) => {

                if (section.name.includes("Peajes")) {
                    const wsRows = [];
                    const concepts = section.concepts || [];
                    const baseHeaders = section.header || [];
                    const merges = [];
                    let currentRow = 0;

                    const headersNoEnergy = baseHeaders.filter(h => h.key !== "total_energy");
                    const headersSubTable1 = headersNoEnergy.filter(h => ["concepto", "tipo", "or", "total_value"].includes(h.key));

                    const facturasStr = concepts.filter(c => c.concepto && c.concepto.startsWith("Factura STR"));
                    
                    // CORRECCIÓN: Filtro ajustado para incluir los prorrateos de Cargo STR Activa
                    const liqSdlStr = concepts.filter(c => 
                        c.concepto === "Cargo SDL Activa" || 
                        c.concepto === "Cargo STR Activa" || 
                        c.concepto === "Total Cargo STR Activa"
                    );
                    
                    const liqReactiva = concepts.filter(c => c.concepto && c.concepto.startsWith("Peaje Energía Reactiva"));

                    const subTables = [
                        { 
                            title: `Facturas STR y Convocatorias (${currentPeriodStr})`, 
                            data: facturasStr, 
                            headers: headersSubTable1,
                            proposito: "Validación de la facturación que recibe Cedenar por concepto de Peajes a nivel del STR por atender la demanda integral"
                        },
                        { 
                            title: `Liquidación SDL y STR (${currentPeriodStr})`, 
                            data: liqSdlStr, 
                            headers: headersNoEnergy,
                            proposito: "Validación exclusiva de peajes por STR y SDL asociados a la atención de fronteras del grupo ECP",
                            nota: "*Nota: La suma del Cargo SDL (asociado al OR) y Cargo STR (asociado a la zona Norte o Centro-Sur) para una frontera corresponde con el valor facturado por el cargo de Distribución"
                        },
                        { 
                            title: `Liquidación Energías Reactivas (${currentPeriodStr})`, 
                            data: liqReactiva, 
                            headers: headersNoEnergy,
                            proposito: "Validación de la facturación por penalización de transporte de energía reactiva "
                        }
                    ];

                    const totalCols = headersNoEnergy.length;
                    const safeCols = Math.max(0, totalCols - 1);

                    subTables.forEach((subTable) => {
                        if (subTable.data.length === 0) return;
                        const currentHeaders = subTable.headers;
                        
                        if (subTable.proposito) {
                            wsRows.push(["Propósito", ...Array(safeCols).fill("")]);
                            merges.push({ s: { r: currentRow, c: 0 }, e: { r: currentRow, c: safeCols } });
                            currentRow++;
                            
                            wsRows.push([subTable.proposito, ...Array(safeCols).fill("")]);
                            merges.push({ s: { r: currentRow, c: 0 }, e: { r: currentRow, c: safeCols } });
                            currentRow++;
                        }

                        const titleRow = Array(totalCols).fill("");
                        titleRow[0] = subTable.title;
                        wsRows.push(titleRow);
                        merges.push({ s: { r: currentRow, c: 0 }, e: { r: currentRow, c: safeCols } });
                        currentRow++;

                        wsRows.push(currentHeaders.map(h => h.name));
                        currentRow++;

                        subTable.data.forEach((concept) => {
                            const dataRow = currentHeaders.map((head) => {
                                const val = concept[head.key];
                                if (head.isNumeric && val !== "-" && val !== null && val !== undefined) {
                                    const num = Number(val);
                                    return !isNaN(num) ? num : val;
                                }
                                return val !== undefined ? val : "-";
                            });
                            wsRows.push(dataRow);
                            currentRow++;
                        });
                        
                        if (subTable.nota) {
                            wsRows.push([subTable.nota, ...Array(safeCols).fill("")]);
                            merges.push({ s: { r: currentRow, c: 0 }, e: { r: currentRow, c: safeCols } });
                            currentRow++;
                        }

                        wsRows.push(Array(totalCols).fill(""));
                        currentRow++;
                    });

                    const worksheet = XLSX.utils.aoa_to_sheet(wsRows);
                    worksheet['!merges'] = merges;
                    autofitColumnsAndFormat(worksheet, wsRows);
                    XLSX.utils.book_append_sheet(workbook, worksheet, "Peajes uso de la red");

                } else if (section.name.includes("Cargos y Liquidación") || section.name.includes("Liquidación Mes Actual")) {
                    const wsRowsLiq = [];
                    const headers = section.header || [];
                    const concepts = section.concepts || [];
                    const totalCols = headers.length > 0 ? headers.length : 1;
                    const mergesLiq = [];
                    let curRow = 0;

                    const subTablasLiq = [
                        { title: `Liquidación Mes (${currentPeriodStr})`, keyType: "value" },
                        { title: `Ajustes Liquidación (${currentPeriodStr})`, keyType: "adjust" },
                        { title: `Neto Liquidación Factura (${currentPeriodStr})`, keyType: "total" }
                    ];

                    subTablasLiq.forEach((sub) => {
                        const titleRow = Array(totalCols).fill("");
                        titleRow[0] = sub.title;
                        wsRowsLiq.push(titleRow);
                        mergesLiq.push({ s: { r: curRow, c: 0 }, e: { r: curRow, c: totalCols - 1 } });
                        curRow++;

                        wsRowsLiq.push(headers.map(h => h.name));
                        curRow++;

                        concepts.forEach((concept) => {
                            const row = headers.map((head) => {
                                if (["concepto", "tipo", "mandatario", "factura"].includes(head.key)) {
                                    return concept[head.key] || "-";
                                }

                                let val;
                                if (sub.keyType === "value") {
                                    val = concept[head.key + "_value"] !== undefined ? concept[head.key + "_value"] : concept["value"];
                                } else if (sub.keyType === "adjust") {
                                    val = concept[head.key + "_adjust"] !== undefined ? concept[head.key + "_adjust"] : concept["adjust"];
                                } else {
                                    val = concept[head.key] !== undefined ? concept[head.key] : concept["total"];
                                }

                                if (val !== "-" && val !== null && val !== undefined) {
                                    const num = Number(val);
                                    return !isNaN(num) ? num : "-";
                                }
                                return "-";
                            });
                            wsRowsLiq.push(row);
                            curRow++;
                        });

                        wsRowsLiq.push(Array(totalCols).fill(""));
                        curRow++;
                    });

                    applyStaticText("Liquidación Mes Actual", wsRowsLiq, mergesLiq, totalCols);

                    const wsLiq = XLSX.utils.aoa_to_sheet(wsRowsLiq);
                    wsLiq['!merges'] = mergesLiq;
                    autofitColumnsAndFormat(wsLiq, wsRowsLiq);
                    XLSX.utils.book_append_sheet(workbook, wsLiq, "Liquidación Mes Actual");

                } else if (section.name.includes("Liquidación Mes Previo")) {
                    const wsRowsPrev = [];
                    const headers = section.header || [];
                    const conceptsPre = section.concepts_pre || [];
                    const conceptsDef = section.concepts_def || [];
                    const totalCols = headers.length > 0 ? headers.length : 1;
                    const colsOffset = Math.max(0, totalCols - 1);
                    const mergesPrev = [];
                    let curRowPrev = 0;

                    const getPreValue = (concept, headKey) => {
                        if (!concept) return "-";
                        if (["concepto", "tipo", "mandatario", "factura"].includes(headKey)) return concept[headKey] || "-";
                        let val = headKey === "total"
                            ? (concept["value"] !== undefined ? concept["value"] : concept["total"])
                            : (concept[headKey + "_value"] !== undefined ? concept[headKey + "_value"] : (concept["value"] !== undefined ? concept["value"] : concept[headKey]));
                        if (val !== "-" && val !== null && val !== undefined) {
                            const num = Number(val);
                            return !isNaN(num) ? num : val;
                        }
                        return "-";
                    };

                    const getDefTotal = (concept, headKey) => {
                        if (!concept) return "-";
                        if (["concepto", "tipo", "mandatario", "factura"].includes(headKey)) return concept[headKey] || "-";
                        const val = concept[headKey] !== undefined ? concept[headKey] : concept["total"];
                        if (val !== "-" && val !== null && val !== undefined) {
                            const num = Number(val);
                            return !isNaN(num) ? num : val;
                        }
                        return "-";
                    };

                    wsRowsPrev.push([`Liquidación Mes Previo Preliminar nr-pre (${prevPeriodStr})`, ...Array(colsOffset).fill("")]);
                    mergesPrev.push({ s: { r: curRowPrev, c: 0 }, e: { r: curRowPrev, c: totalCols - 1 } });
                    curRowPrev++;
                    wsRowsPrev.push(headers.map(h => h.name));
                    curRowPrev++;

                    if (conceptsPre.length > 0) {
                        conceptsPre.forEach((c) => {
                            wsRowsPrev.push(headers.map(h => getPreValue(c, h.key)));
                            curRowPrev++;
                        });
                    } else {
                        wsRowsPrev.push(["Sin datos preliminares", ...Array(colsOffset).fill("-")]);
                        curRowPrev++;
                    }
                    wsRowsPrev.push(Array(totalCols).fill(""));
                    curRowPrev++;

                    wsRowsPrev.push([`Liquidación Mes Previo Definitiva nr-def (${prevPeriodStr})`, ...Array(colsOffset).fill("")]);
                    mergesPrev.push({ s: { r: curRowPrev, c: 0 }, e: { r: curRowPrev, c: totalCols - 1 } });
                    curRowPrev++;
                    wsRowsPrev.push(headers.map(h => h.name));
                    curRowPrev++;

                    if (conceptsDef.length > 0) {
                        conceptsDef.forEach((c) => {
                            wsRowsPrev.push(headers.map(h => getDefTotal(c, h.key)));
                            curRowPrev++;
                        });
                    } else {
                        wsRowsPrev.push(["Sin datos definitivos", ...Array(colsOffset).fill("-")]);
                        curRowPrev++;
                    }
                    wsRowsPrev.push(Array(totalCols).fill(""));
                    curRowPrev++;

                    wsRowsPrev.push([`Resumen Diferencia Liquidación (${prevPeriodStr})`, ...Array(colsOffset).fill("")]);
                    mergesPrev.push({ s: { r: curRowPrev, c: 0 }, e: { r: curRowPrev, c: totalCols - 1 } });
                    curRowPrev++;
                    wsRowsPrev.push(headers.map(h => h.name));
                    curRowPrev++;

                    const totalRowPre = conceptsPre.find(c => c.concepto === "TOTAL LIQUIDACION");
                    const totalRowDef = conceptsDef.find(c => c.concepto === "TOTAL LIQUIDACION");

                    const rowPreTotal = headers.map((head) => {
                        if (head.key === "concepto") return "Liquidacion Preliminar Total";
                        if (["tipo", "mandatario", "factura"].includes(head.key)) return "-";
                        const val = getPreValue(totalRowPre, head.key);
                        return typeof val === 'number' ? val : 0;
                    });

                    const rowDefTotal = headers.map((head) => {
                        if (head.key === "concepto") return "Liquidacion Definitiva Total";
                        if (["tipo", "mandatario", "factura"].includes(head.key)) return "-";
                        const val = getDefTotal(totalRowDef, head.key);
                        return typeof val === 'number' ? val : 0;
                    });

                    const rowDiffTotal = headers.map((head, idx) => {
                        if (head.key === "concepto") return "Diferencia Total (Definitiva - Preliminar)";
                        if (["tipo", "mandatario", "factura"].includes(head.key)) return "-";
                        const valPreBase = Number(rowPreTotal[idx] || 0);
                        const valDefTotal = Number(rowDefTotal[idx] || 0);
                        return valDefTotal - valPreBase;
                    });

                    [rowPreTotal, rowDefTotal, rowDiffTotal].forEach(r => {
                        wsRowsPrev.push(r);
                        curRowPrev++;
                    });

                    applyStaticText("Liquidación Mes Previo", wsRowsPrev, mergesPrev, totalCols);

                    const wsPrev = XLSX.utils.aoa_to_sheet(wsRowsPrev);
                    wsPrev['!merges'] = mergesPrev;
                    autofitColumnsAndFormat(wsPrev, wsRowsPrev);
                    XLSX.utils.book_append_sheet(workbook, wsPrev, "Liquidación Mes Previo");

                } else if (section.name.includes("Balance ECP")) {
                    const wsRowsECP = [];
                    const headers = section.header || [];
                    const concepts = section.concepts || [];
                    const headerNeto = section.header_neto || [];
                    const conceptsNeto = section.concepts_neto || [];
                    const mergesECP = [];
                    let curRowECP = 0;

                    if (headers.length > 0) {
                        wsRowsECP.push(headers.map(h => h.name));
                        curRowECP++;
                        concepts.forEach((c) => {
                            const row = headers.map((head) => {
                                const val = c[head.key];
                                if (head.isNumeric && val !== "-" && val !== null && val !== undefined) {
                                    const num = Number(val);
                                    return !isNaN(num) ? num : val;
                                }
                                return val !== undefined ? val : "-";
                            });
                            wsRowsECP.push(row);
                            curRowECP++;
                        });
                    }

                    wsRowsECP.push(Array(headers.length || 1).fill(""));
                    curRowECP++;

                    if (headerNeto.length > 0 && conceptsNeto.length > 0) {
                        wsRowsECP.push(["Subtabla Ingresos por Comercialización", ...Array(headers.length - 1).fill("")]);
                        mergesECP.push({ s: { r: curRowECP, c: 0 }, e: { r: curRowECP, c: headers.length - 1 } });
                        curRowECP++;

                        wsRowsECP.push(headerNeto.map(h => h.name));
                        curRowECP++;

                        conceptsNeto.forEach((cn) => {
                            const rowNeto = headerNeto.map((head) => {
                                const val = cn[head.key];
                                if (head.isNumeric && val !== "-" && val !== null && val !== undefined) {
                                    const num = Number(val);
                                    return !isNaN(num) ? num : val;
                                }
                                return val !== undefined ? val : "-";
                            });
                            const paddedRowNeto = [...rowNeto, ...Array(Math.max(0, headers.length - rowNeto.length)).fill("")];
                            wsRowsECP.push(paddedRowNeto);
                            curRowECP++;
                        });
                    }

                    applyStaticText("Balance ECP", wsRowsECP, mergesECP, headers.length);

                    const wsECP = XLSX.utils.aoa_to_sheet(wsRowsECP);
                    if (mergesECP.length > 0) wsECP['!merges'] = mergesECP;
                    autofitColumnsAndFormat(wsECP, wsRowsECP);
                    XLSX.utils.book_append_sheet(workbook, wsECP, "Balance ECP");

                } else {
                    const wsRows = [];
                    const mergesGeneric = [];
                    const totalCols = section.header.length;
                    const safeCols = Math.max(0, totalCols - 1);

                    // Insertar texto estático inicial
                    const textData = staticText[section.name];
                    if (textData && textData.proposito) {
                        wsRows.push(
                            ["Propósito", ...Array(safeCols).fill("")],
                            [textData.proposito, ...Array(safeCols).fill("")],
                            Array(totalCols).fill("")
                        );
                        mergesGeneric.push(
                            { s: { r: 0, c: 0 }, e: { r: 0, c: safeCols } },
                            { s: { r: 1, c: 0 }, e: { r: 1, c: safeCols } }
                        );
                    }

                    wsRows.push(section.header.map(head => head.name));
                    
                    section.concepts.forEach((concept) => {
                        const dataRow = section.header.map((head) => {
                            const val = concept[head.key];
                            if (head.isNumeric && val !== "-" && val !== null && val !== undefined) {
                                const num = Number(val);
                                return !isNaN(num) ? num : val;
                            }
                            return val !== undefined ? val : "-";
                        });
                        wsRows.push(dataRow);
                    });

                    // Insertar notas finales
                    if (textData && textData.notas && textData.notas.length > 0) {
                        wsRows.push(Array(totalCols).fill(""));
                        textData.notas.forEach(nota => {
                            wsRows.push([nota, ...Array(safeCols).fill("")]);
                            const lastRowIdx = wsRows.length - 1;
                            mergesGeneric.push({ s: { r: lastRowIdx, c: 0 }, e: { r: lastRowIdx, c: safeCols } });
                        });
                    }

                    const worksheet = XLSX.utils.aoa_to_sheet(wsRows);
                    if (mergesGeneric.length > 0) worksheet['!merges'] = mergesGeneric;

                    let sheetName = section.name.replace(/[\\/*?:\r\n\[\]]/g, "").slice(0, 30);
                    let duplicateCounter = 1;
                    let finalSheetName = sheetName;
                    while (usedSheetNames.has(finalSheetName)) {
                        finalSheetName = `${sheetName.slice(0, 27)}_${duplicateCounter}`;
                        duplicateCounter++;
                    }
                    usedSheetNames.add(finalSheetName);
                    
                    autofitColumnsAndFormat(worksheet, wsRows);
                    XLSX.utils.book_append_sheet(workbook, worksheet, finalSheetName);
                }
            });
        }

        return workbook;
    };

    const downloadEnergyBalance = async () => {
        if ((!balanceSections || balanceSections.length === 0) && !enterpriseBalance) return;
        setDownloading(true);

        try {
            const data = await fetchPortfolioData("STRICT_CORRECT");
            if (data && data.Sections) {
                const workbook = generateExcelWorkbook(data.Sections);
                const periodFormatted = getFormattedPeriod(props.periodDate, 1);
                const version = props.tariffCalcVersion.includes("def") ? "Definitivo" : "Preliminar" 
                const fileName = `${version} Balance_Cedenar_Ecopetrol_${periodFormatted}.xlsx`;
                XLSX.writeFile(workbook, fileName);
            }
        } catch (error) {
            console.error("Error generando Excel STRICT_CORRECT:", error);
            setFeedbackMsg("Ocurrió un error al generar el archivo Excel en modo estricto.");
            setFeedbackDialog(true);
        } finally {
            setDownloading(false);
        }
    };

    const downloadEnergyBalanceLegacy = async () => {
        if ((!balanceSections || balanceSections.length === 0) && !enterpriseBalance) return;
        setDownloadingLegacy(true);

        try {
            const data = await fetchPortfolioData("LEGACY_DIAN_COMPATIBLE");
            if (data && data.Sections) {
                const workbook = generateExcelWorkbook(data.Sections);
                const periodFormatted = getFormattedPeriod(props.periodDate, 1);
                const version = props.tariffCalcVersion.includes("def") ? "Definitivo" : "Preliminar" 
                const fileName = `${version} Balance_Cedenar_Ecopetrol_${periodFormatted}.xlsx`;
                XLSX.writeFile(workbook, fileName);
            }
        } catch (error) {
            console.error("Error generando Excel LEGACY_DIAN_COMPATIBLE:", error);
            setFeedbackMsg("Ocurrió un error al generar el archivo Excel en modo legacy.");
            setFeedbackDialog(true);
        } finally {
            setDownloadingLegacy(false);
        }
    };

    const showFeedbackDialog = () => (
        <Dialog open={feedbackDialog} onClose={handleFeedbackDialogClose} className={classes.formText}>
            <DialogTitle className={classes.dialogTitle} style={{ textAlign: 'center' }}>Aviso</DialogTitle>
            <DialogContent className={classes.dialogContent}>
                <DialogContentText>{feedbackMsg}</DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleFeedbackDialogClose} color="primary" autoFocus>Aceptar</Button>
            </DialogActions>
        </Dialog>
    );

    useEffect(() => {
        document.title = "Balance energético portafolios no regulados";
        let nextPeriodDate = addMonths(props.periodDate, 1);
        authFetch("/api/calc-energy-balance?username=" + props.username +
            '&period=' + String(nextPeriodDate.getMonth() + 1) +
            "&year=" + nextPeriodDate.getFullYear() +
            "&version=def").then(response => {
                if (response.status === 422) {
                    logout();
                    return null;
                }
                return response.json();
            }).then(response => {
                if (response && response.Concepts) {
                    setEnterpriseBalance(response.Concepts);
                }
            });

        authFetch("/api/check-portfolios-list?username=" + props.username + "&with_details=true").then(response => {
            if (response.status === 422) {
                logout();
                return null;
            }
            return response.json();
        }).then(response => {
            if (response && response.portfolios) {
                let portfolioNames = response.portfolios.flatMap((portfolio) => [portfolio.name]);
                setPortfolios(Array.from(new Set(portfolioNames)));
            }
        });
    }, [props.periodDate, props.tariffCalcVersion]);

    useEffect(() => {
        if (selectedPortfolio !== '' && enterpriseBalance) {
            setIsLoading(true);
            fetchPortfolioData("STRICT_CORRECT").then(response => {
                if (response && response.Sections) {
                    setBalanceSections(response.Sections);
                }
                setIsLoading(false);
            }).catch(error => {
                setIsLoading(false);
                setFeedbackMsg("Ocurrió un error al cargar los datos.");
                setFeedbackDialog(true);
            });
        }
    }, [selectedPortfolio, props.tariffCalcVersion, props.periodDate]);

    return (
        <div className={classes.root}>
            {showFeedbackDialog()}
            <Zoom in timeout={800}>
                <Grid container alignItems="center" justify='space-evenly' spacing={2}>
                    <Grid item xs={11}>
                        <Typography variant="subtitle1" className={classes.sectionTitle}>
                            {'Balance energético Portafolio para tarifas: ' + addMonths(props.periodDate, 1).toISOString().slice(0, 7)}
                        </Typography>
                    </Grid>

                    <Grid item xs={2}>
                        <TextField
                            select
                            fullWidth
                            label="Portafolio"
                            variant="filled"
                            helperText={selectedPortfolio ? null : 'Seleccione un portafolio'}
                            size="small"
                            className={classes.customFilled}
                            value={selectedPortfolio}
                            onChange={(e) => setSelectedPortfolio(e.target.value)}
                        >
                            {portfolios.length > 0 ?
                                portfolios.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)
                                : <MenuItem value=""></MenuItem>
                            }
                        </TextField>
                    </Grid>

                    {isLoading ? (
                        <Grid item xs={11} style={{ marginTop: 30 }}>
                            <CircularProgress color="primary" />
                        </Grid>
                    ) : (
                        <Grid item xs={11}>
                            {enterpriseBalance && enterpriseBalance.length > 0 && (
                                <Accordion style={{ marginBottom: 16 }} elevation={0} TransitionProps={{ unmountOnExit: true }} className={classes.accordionStyle}>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                        <Typography style={{ fontWeight: 'bold' }}>Balance General</Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <TableContainer component={Paper} elevation={0}>
                                            <Table aria-label="table-balance-general">
                                                <TableHead>
                                                    <TableRow>
                                                        <StyledTableCell align="center" style={{ width: "25%" }}>Concepto</StyledTableCell>
                                                        <StyledTableCell align="center" style={{ width: "12%" }}>Compras kWh</StyledTableCell>
                                                        <StyledTableCell align="center" style={{ width: "12%" }}>Compras $/kWh</StyledTableCell>
                                                        <StyledTableCell align="center" style={{ width: "12%" }}>Compras $</StyledTableCell>
                                                        <StyledTableCell align="center" style={{ width: "12%" }}>Ventas kWh</StyledTableCell>
                                                        <StyledTableCell align="center" style={{ width: "12%" }}>Ventas $/kWh</StyledTableCell>
                                                        <StyledTableCell align="center" style={{ width: "12%" }}>Ventas $</StyledTableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {enterpriseBalance.map((concept, idx) => (
                                                        <StyledTableRow key={idx}>
                                                            <StyledTableCell align="center" style={{ height: 55 }}>{concept.Name}</StyledTableCell>
                                                            {concept.Values && concept.Values.map((val, sidx) => (
                                                                <StyledTableCell key={sidx} align="center">
                                                                    {val > 0 ? val.toLocaleString('en-US', { minimumFractionDigits: sidx === 1 ? 5 : 2, maximumFractionDigits: sidx === 1 ? 5 : 2 }) : '-'}
                                                                </StyledTableCell>
                                                            ))}
                                                        </StyledTableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </AccordionDetails>
                                </Accordion>
                            )}

                            {balanceSections && balanceSections.length > 0 ? (
                                balanceSections.map((section, sIdx) => (
                                    <Accordion key={sIdx} style={{ marginBottom: 16 }} elevation={0} TransitionProps={{ unmountOnExit: true }} className={classes.accordionStyle}>
                                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                            <Typography style={{ fontWeight: 'bold' }}>{section.name}</Typography>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <Grid container spacing={2}>
                                                <Grid item xs={12}>
                                                    <TableContainer component={Paper} elevation={0}>
                                                        <Table aria-label={`table-${section.name}`}>
                                                            <TableHead>
                                                                <TableRow>
                                                                    {section.header && section.header.map((head, hIdx) => (
                                                                        <StyledTableCell key={hIdx} align="center" style={{ width: `${100 / section.header.length}%` }}>
                                                                            {head.name}
                                                                        </StyledTableCell>
                                                                    ))}
                                                                </TableRow>
                                                            </TableHead>
                                                            <TableBody>
                                                                {/* CORRECCIÓN FILTRO REACT UI CON GUARDIA DE SEGURIDAD */}
                                                                {section.concepts && (section.name.includes("Peajes") 
                                                                    ? section.concepts.filter(c => c.concepto === "Cargo SDL Activa" || c.concepto === "Cargo STR Activa" || c.concepto === "Total Cargo STR Activa")
                                                                    : section.concepts
                                                                ).map((concept, cIdx) => (
                                                                    <StyledTableRow key={cIdx}>
                                                                        {section.header.map((head, hIdx) => {
                                                                            const rawValue = concept[head.key];
                                                                            const isNumeric = head.isNumeric;
                                                                            const formattedValue = isNumeric && rawValue !== "-" && rawValue !== null && rawValue !== undefined
                                                                                ? (rawValue > 0 ? Number(rawValue).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0')
                                                                                : rawValue;

                                                                            return (
                                                                                <StyledTableCell key={hIdx} align="center" style={{ height: 55 }}>
                                                                                    {formattedValue}
                                                                                </StyledTableCell>
                                                                            );
                                                                        })}
                                                                    </StyledTableRow>
                                                                ))}
                                                            </TableBody>
                                                        </Table>
                                                    </TableContainer>
                                                </Grid>

                                                {section.header_neto && section.concepts_neto && (
                                                    <Grid item xs={12} style={{ marginTop: 16 }}>
                                                        <Typography variant="subtitle2" style={{ fontWeight: 'bold', marginBottom: 8, textAlign: 'left' }}>
                                                            Ingresos por Comercialización
                                                        </Typography>
                                                        <TableContainer component={Paper} elevation={0}>
                                                            <Table aria-label="table-ingresos-neto">
                                                                <TableHead>
                                                                    <TableRow>
                                                                        {section.header_neto.map((head, hIdx) => (
                                                                            <StyledTableCell key={hIdx} align="center" style={{ width: `${100 / section.header_neto.length}%` }}>
                                                                                {head.name}
                                                                            </StyledTableCell>
                                                                        ))}
                                                                    </TableRow>
                                                                </TableHead>
                                                                <TableBody>
                                                                    {section.concepts_neto.map((conceptNeto, cIdx) => (
                                                                        <StyledTableRow key={cIdx}>
                                                                            {section.header_neto.map((head, hIdx) => {
                                                                                const rawValue = conceptNeto[head.key];
                                                                                const isNumeric = head.isNumeric;
                                                                                const formattedValue = isNumeric && rawValue !== "-" && rawValue !== null && rawValue !== undefined
                                                                                    ? (rawValue > 0 ? Number(rawValue).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0')
                                                                                    : rawValue;

                                                                                return (
                                                                                    <StyledTableCell key={hIdx} align="center" style={{ height: 55 }}>
                                                                                        {formattedValue}
                                                                                    </StyledTableCell>
                                                                                );
                                                                            })}
                                                                        </StyledTableRow>
                                                                    ))}
                                                                </TableBody>
                                                            </Table>
                                                        </TableContainer>
                                                    </Grid>
                                                )}
                                            </Grid>
                                        </AccordionDetails>
                                    </Accordion>
                                ))
                            ) : (
                                selectedPortfolio && !balanceSections && (
                                    <Typography variant="body1" color='primary' style={{ marginTop: 10 }}>
                                        {'No hay datos para el portafolio/periodo seleccionado'}
                                    </Typography>
                                )
                            )}
                        </Grid>
                    )}

                    {(enterpriseBalance || balanceSections) && (
                        <Grid item xs={8} style={{ marginTop: 15, display: 'flex', justifyContent: 'center', gap: '16px' }}>
                            {downloading ? (
                                <CircularProgress color="primary" />
                            ) : (
                                <Button color='primary' variant='contained' startIcon={<ArrowDropDownCircleIcon />} onClick={downloadEnergyBalance}>
                                    Descargar (Modo Estricto)
                                </Button>
                            )}

                            {downloadingLegacy ? (
                                <CircularProgress color="secondary" />
                            ) : (
                                <Button color='secondary' variant='contained' startIcon={<ArrowDropDownCircleIcon />} onClick={downloadEnergyBalanceLegacy}>
                                    Descargar (Modo Legacy / DIAN)
                                </Button>
                            )}
                        </Grid>
                    )}
                </Grid>
            </Zoom>
        </div>
    );
}